import { BizError } from "@/models/biz_model"
import { KCSDK } from "@/sdk"
import { useAppDispatch, useAppSelector } from "@/store"
import {
  selectIsChromiumBlinkBrowser,
  selectIsSafariBrowser,
} from "@/stores/app_slice"
import { startGetConnStats, stopGetConnStats } from "@/stores/conn_stats_slice"
import {
  LoginStatus,
  updateLoginStatus,
  changeViewType,
  ViewType,
  setLoginApiVersion,
  LoginApiVersion,
} from "@/stores/login_slice"
import { AudioUsecase, useInitAudioInput } from "@/usecases/audio"
import { VideoUsecase, useInitVideoInput } from "@/usecases/video"
import { ClipboardUsecase } from "@/usecases/clipboard"
import { ConnectionUseCase } from "@/usecases/connection"
import { DisplayUseCase } from "@/usecases/display"
import { useReportKeyboard } from "@/usecases/keyboard"
import { useRemoteDeskMouse } from "@/usecases/mouse"
import { debounce, isUserActive, mytest } from "@/utils/tools"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { ToastId, useToast } from "@chakra-ui/react"
import { useNavigate } from "react-router-dom"

import { clientDigit, isAssistLogin } from "@/pages/login/login_hook"
import { VirtualMouseEvents } from "@/hooks/use_virtual_mouse"

import {
  selectIsUseMacMouseWheelDir,
  selectResolutionItem,
  selectIsFullScreen,
  selectIsThreeDMouse,
} from "@/stores/remote_desk_state_slice"
import { updateMousePos } from "@/stores/mouse_slice"
import { UserUsecase } from "@/usecases/user"
import { getOS, OS } from "@/utils/os"
import cameraOffIcon from "@/assest/ic_camera_off.svg"
import microphoneOffIcon from "@/assest/ic_microphone_off.svg"
import cameraOnIcon from "@/assest/ic_camera_on.svg"
import microphoneOnIcon from "@/assest/ic_microphone_on.svg"

const exitPointerLockIfSupported = () => {
  if (typeof document.exitPointerLock === "function") {
    document.exitPointerLock()
  }
}

const requestPointerLockIfSupported = (element: HTMLVideoElement | null) => {
  if (typeof element?.requestPointerLock === "function") {
    element.requestPointerLock()
  }
}

export const RemoteDeskView: React.FC = () => {
  const toast = useToast()
  const dispatch = useAppDispatch()
  const micToastId = useRef<ToastId | null>(null)
  const cameraToastId = useRef<ToastId | null>(null)
  const navigatorTwo = useNavigate()
  const isUseMacMouseWheelDir = useAppSelector(selectIsUseMacMouseWheelDir)

  const pointerLockActivatedAt = useRef<number | null>(null)
  const pointerLockActivatedAtTwo = useRef<number | null>(null)
  const isMac = getOS() === OS.MacOS
  const isChromiumBlinkVal = useAppSelector(selectIsChromiumBlinkBrowser)
  const isSafariBrowserVal = useAppSelector(selectIsSafariBrowser)
  const canUseMacFullScreenChromiumCompat =
    isMac && mytest && isChromiumBlinkVal

  const isFullScreen = useAppSelector(selectIsFullScreen)
  const isThreeDMouse = useAppSelector(selectIsThreeDMouse)
  const shouldUseMacFullScreenVirtualMouse =
    isFullScreen && canUseMacFullScreenChromiumCompat
  const [isGameCloudComputer, setIsGameCloudComputer] = useState(
    ConnectionUseCase.isGameCloudComputer()
  )

  const isFullScreenRef = useRef(isFullScreen)
  const isThreeDMouseRef = useRef(isThreeDMouse)

  useEffect(() => {
    isFullScreenRef.current = isFullScreen
  }, [isFullScreen])

  useEffect(() => {
    isThreeDMouseRef.current = isThreeDMouse
  }, [isThreeDMouse])

  useEffect(() => {
    setIsGameCloudComputer(ConnectionUseCase.isGameCloudComputer())
    ConnectionUseCase.setGameCloudComputerListener((value) => {
      setIsGameCloudComputer(value)
    })
  }, [])

  setTimeout(() => {
    KCSDK.sendHeartbeatData("data-channel-heart-beat-text")
  }, 100)

  // 内存中的分辨率
  const resolutionItem = useAppSelector(selectResolutionItem)

  const { width: memoryWidth, height: memoryHeight } = resolutionItem

  // 使用 useRef 来存储最新的 state 值
  const latestMemoryWidthRef = useRef(memoryWidth)
  const latestMemoryHeightRef = useRef(memoryHeight)

  // 每当 state 更新时更新 ref 的值
  useEffect(() => {
    latestMemoryWidthRef.current = memoryWidth
    latestMemoryHeightRef.current = memoryHeight
  }, [memoryWidth, memoryHeight])

  // 设置视频流分辨率
  const setResolution = useCallback((): void => {
    // 考虑到系统缩放，将逻辑像素转换成物理像素
    const initWidth = window.innerWidth * window.devicePixelRatio
    const initHeight = window.innerHeight * window.devicePixelRatio

    const width =
      latestMemoryWidthRef.current > 0
        ? latestMemoryWidthRef.current
        : initWidth
    const height =
      latestMemoryHeightRef.current > 0
        ? latestMemoryHeightRef.current
        : initHeight

    if (!isAssistLogin) {
      DisplayUseCase.setRemoteStreamResolution(width, height)
    }
  }, [])

  // 视频组件
  const videoRef = useRef<HTMLVideoElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const hasReportedFirstFrame = useRef(false)
  const [reconnectingFrame, setReconnectingFrame] = useState<string | null>(
    null
  )

  useEffect(() => {
    const connectionCallback = {
      // 首帧消息按连接维度上报，重连后允许新视频流再次上报。
      onReconnect: () => {
        hasReportedFirstFrame.current = false
        const video = videoRef.current
        if (
          video === null ||
          video.videoWidth === 0 ||
          video.videoHeight === 0
        ) {
          return
        }

        try {
          const canvas = document.createElement("canvas")
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          canvas.getContext("2d")?.drawImage(video, 0, 0)
          setReconnectingFrame(canvas.toDataURL("image/jpeg", 0.85))
        } catch (error) {
          console.warn("Capture reconnect video frame error: ", error)
        }
      },
      onDisconnect: () => {
        hasReportedFirstFrame.current = false
        setReconnectingFrame(null)
      },
    }
    ConnectionUseCase.addConnCallback(connectionCallback)
    return () => ConnectionUseCase.removeConnCallback(connectionCallback)
  }, [])

  useEffect(() => {
    return AudioUsecase.addAudioInputOperationListener(({ isOpen, phase }) => {
      const title =
        phase === "start"
          ? isOpen
            ? "正在打开麦克风"
            : "正在关闭麦克风"
          : phase === "success"
            ? isOpen
              ? "麦克风已开启"
              : "麦克风已关闭"
            : isOpen
              ? "麦克风开启失败"
              : "麦克风关闭失败"
      const options = {
        title,
        status:
          phase === "start"
            ? ("loading" as const)
            : phase === "success"
              ? isOpen
                ? ("success" as const)
                : ("success" as const)
              : ("error" as const),
        icon:
          phase === "success" ? (
            <img
              src={isOpen ? microphoneOnIcon : microphoneOffIcon}
              alt=""
              width="20"
              height="20"
            />
          ) : undefined,
        duration: phase === "start" ? null : 2000,
        isClosable: true,
        position: "top-right" as const,
        containerStyle: { width: "190px" },
      }

      if (
        phase !== "start" &&
        micToastId.current !== null &&
        toast.isActive(micToastId.current)
      ) {
        toast.update(micToastId.current, options)
        return
      }

      if (phase === "start" && micToastId.current !== null) {
        toast.close(micToastId.current)
      }
      micToastId.current = toast(options)
    })
  }, [toast])

  useEffect(() => {
    return VideoUsecase.addVideoInputOperationListener(({ isOpen, phase }) => {
      const title =
        phase === "start"
          ? isOpen
            ? "正在打开摄像头"
            : "正在关闭摄像头"
          : phase === "success"
            ? isOpen
              ? "摄像头已开启"
              : "摄像头已关闭"
            : isOpen
              ? "摄像头开启失败"
              : "摄像头关闭失败"
      const options = {
        title,
        status:
          phase === "start"
            ? ("loading" as const)
            : phase === "success"
              ? isOpen
                ? ("success" as const)
                : ("success" as const)
              : ("error" as const),
        icon:
          phase === "success" ? (
            <img
              src={isOpen ? cameraOnIcon : cameraOffIcon}
              alt=""
              width="20"
              height="20"
            />
          ) : undefined,
        duration: phase === "start" ? null : 2000,
        isClosable: true,
        position: "top-right" as const,
        containerStyle: { width: "190px" },
      }

      if (
        phase !== "start" &&
        cameraToastId.current !== null &&
        toast.isActive(cameraToastId.current)
      ) {
        toast.update(cameraToastId.current, options)
        return
      }

      if (phase === "start" && cameraToastId.current !== null) {
        toast.close(cameraToastId.current)
      }
      cameraToastId.current = toast(options)
    })
  }, [toast])

  // 视频组件大小, scale 为视频分辨率与屏幕分辨率的比例
  interface VideoViewLayout {
    width: number
    height: number
    left: number
    top: number
    scale: number
  }

  const [videoViewLayout, setVideoViewLayout] = useState<VideoViewLayout>({
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    scale: 1,
  })
  // 视频流分辨率
  const videoResolution = useRef({ width: 0, height: 0 })

  // 处理鼠标事件
  const {
    onMouseEnter,
    onMouseLeave,
    onMouseMove,
    onMouseDown,
    onMouseUp,
    onWheel,
    onClick,
  } = useRemoteDeskMouse({
    scale: videoViewLayout.scale,
    left: videoViewLayout.left,
    top: videoViewLayout.top,
  })

  // 同步本地粘贴板到远端
  ClipboardUsecase.useSyncLocalCPToRemote()

  useEffect(() => {
    // 是否为个人用户登录
    const isPCLogin = UserUsecase.isPCLogin()

    // 返回登录页面
    const goBack = () => {
      if (isPCLogin) {
        if (clientDigit === "digit") {
          // 数字人直播
          const hostname: string = location.hostname
          let digitUrl: string
          if (hostname === "localhost") {
            digitUrl = "http://localhost:3001"
          } else if (hostname === "web.aipc.ksyun.com") {
            digitUrl = "https://digit.ked.ksyun.com"
          } else if (hostname === "webpre.ked.ksyun.com") {
            digitUrl = "https://digit-pre.ked.ksyun.com"
          } else {
            digitUrl = "https://digit.ked.ksyun.com"
          }
          window.open(`${digitUrl}/login`, "_self")
        } else {
          // 个人用户退出时,回到登录页
          // 重置登录页视图为账号登录
          dispatch(changeViewType(ViewType.login))
          dispatch(setLoginApiVersion(LoginApiVersion.newApi))
          navigatorTwo("/login", { replace: true })
        }
      } else {
        // 其他登录方式退出时，返回到登录页面
        // 重置登录页视图为账号登录
        dispatch(changeViewType(ViewType.login))
        dispatch(setLoginApiVersion(LoginApiVersion.newApi))
        navigatorTwo("/login", { replace: true })
      }
    }

    if (!ConnectionUseCase.isPeerConnected()) {
      // 未连接成功，通常发生在页面刷新后
      goBack()
      return
    }

    // 监听视频流分辨率变化
    const onVideoStreamResize = (event: Event): void => {
      const { videoWidth, videoHeight } = event.target as HTMLVideoElement
      if (
        videoWidth === videoResolution.current.width &&
        videoHeight === videoResolution.current.height
      ) {
        return
      }
      // 保存当前视频流的分辨率
      videoResolution.current = { width: videoWidth, height: videoHeight }
      // 更新视频组件尺寸和偏移量
      updateVideoLayout()
    }

    // 设置视频流分辨率, 有防抖处理
    const setResolutionWithDebounce = debounce(setResolution, 1000)

    let scaleRatioRestoreTimerId: number | null = null
    const restoreScaleRatioAfterReconnect = (attempt = 0): void => {
      const resolution = videoResolution.current
      if (resolution.width === 0 || resolution.height === 0) return

      void DisplayUseCase.getScaleRatioConfig(resolution).then((config) => {
        if (config !== null || attempt >= 3) return
        scaleRatioRestoreTimerId = window.setTimeout(() => {
          scaleRatioRestoreTimerId = null
          restoreScaleRatioAfterReconnect(attempt + 1)
        }, 500)
      })
    }

    const scheduleScaleRatioRestore = (): void => {
      if (scaleRatioRestoreTimerId !== null) {
        window.clearTimeout(scaleRatioRestoreTimerId)
      }
      scaleRatioRestoreTimerId = window.setTimeout(() => {
        scaleRatioRestoreTimerId = null
        restoreScaleRatioAfterReconnect()
      }, 300)
    }

    // 设置视频组件布局
    const updateVideoLayout = (): void => {
      let left = 0
      let top = 0
      const winWidth = window.innerWidth
      const winHeight = window.innerHeight
      const videoWidth = videoResolution.current.width
      const videoHeight = videoResolution.current.height

      if (winWidth === 0 || winHeight === 0) {
        console.warn(`Window resize to: ${winWidth}x${winHeight}`)
        return
      }
      if (videoWidth === 0 || videoHeight === 0) {
        console.warn(
          `Window resize, but video resolution is: ${videoWidth}x${videoHeight}`
        )
        return
      }

      const windowRatio = winWidth / winHeight
      const videoRatio = videoWidth / videoHeight
      let scale = 1
      if (videoRatio >= windowRatio) {
        const height = winWidth / videoRatio
        top = (winHeight - height) / 2
        scale = videoWidth / winWidth
      } else {
        const width = winHeight * videoRatio
        left = (winWidth - width) / 2
        scale = videoHeight / winHeight
      }
      setVideoViewLayout({
        width: winWidth,
        height: winHeight,
        top,
        left,
        scale,
      })
    }

    ConnectionUseCase.setMediaStreamCallback((video, audio) => {
      if (videoRef.current !== null) {
        videoRef.current.srcObject = video
        videoRef.current.addEventListener("resize", onVideoStreamResize)
      }
      if (audioRef.current !== null && audio !== null) {
        audioRef.current.srcObject = audio
        audioRef.current.volume = 1
      }

      // 初始化分辨率
      setResolution()

      // 获取视频流分辨率
      const deskStream = video.getVideoTracks()[0]
      if (!deskStream) {
        console.error("No video stream")
        return
      }
      const { width, height } = deskStream.getSettings()
      if (
        width === 0 ||
        width === undefined ||
        height === 0 ||
        height === undefined
      ) {
        return
      }
      // 保存当前视频流的分辨率
      videoResolution.current = { width, height }
      // 记录当前分辨率下的缩放比，供后续重连恢复。
      void DisplayUseCase.getScaleRatioConfig(videoResolution.current)
      // 更新视频组件尺寸和偏移量
      updateVideoLayout()
    })

    // 监听窗口大小变化
    const onWindowResize = (): void => {
      // 更新视频组件尺寸
      updateVideoLayout()
      // 调整视频流分辨率
      setResolutionWithDebounce()
    }
    window.addEventListener("resize", onWindowResize)

    // 处理页面关闭
    const handleUnload = (event: BeforeUnloadEvent): void => {
      // event.stopPropagation()
      // Included for legacy support, e.g. Chrome/Edge < 119
      // event.returnValue = true
      // 页面关闭时退出云桌面
      console.log("Window unload, exit remote desktop")
      ConnectionUseCase.exitRemoteDesk()
    }
    // 监听页面关闭事件
    window.addEventListener("beforeunload", handleUnload)

    // 开始获取连接统计信息
    dispatch(startGetConnStats())

    // 远端断开连接时回到登录页面
    const connectionCallback = {
      onConnected: () => {
        scheduleScaleRatioRestore()
      },
      onDisconnect: (errCode?: number) => {
        if (errCode !== undefined) {
          const msg = new BizError(errCode).displayErrorMsg
          dispatch(
            updateLoginStatus({
              status: LoginStatus.error,
              msg,
            })
          )
        }
        goBack()
      },
    }
    ConnectionUseCase.addConnCallback(connectionCallback)

    let unmuteTimmerId: number | null = null
    // 当网页有交互时，执行 unmute 操作，返回是否 unmute 成功
    const tryUnmute = (): boolean => {
      if (isUserActive() && audioRef?.current?.muted) {
        audioRef.current.muted = false
        audioRef.current.play()
        if (unmuteTimmerId !== null) {
          window.clearInterval(unmuteTimmerId)
          unmuteTimmerId = null
        }
        return true
      }
      return false
    }
    // 轮训检查网页是否有交互，有交互则 unmute video
    if (!tryUnmute()) {
      unmuteTimmerId = window.setInterval(() => {
        tryUnmute()
      }, 1000)
    }

    const dispose = (): void => {
      // 移除监听页面关闭
      window.removeEventListener("beforeunload", handleUnload)
      // 移除监听窗口变化
      window.removeEventListener("resize", onWindowResize)
      // 移除监听视频流分辨率变化
      videoRef.current?.removeEventListener("resize", onVideoStreamResize)
      // 关闭视频流
      console.log("RemoteDesk component dispose, exit remote desktop")
      ConnectionUseCase.exitRemoteDesk()
      // 停止获取统计信息
      dispatch(stopGetConnStats())
      // 停止轮训 unmute video
      if (unmuteTimmerId !== null) {
        window.clearInterval(unmuteTimmerId)
      }
      if (scaleRatioRestoreTimerId !== null) {
        window.clearTimeout(scaleRatioRestoreTimerId)
      }
      ConnectionUseCase.removeConnCallback(connectionCallback)
    }

    return dispose
  }, [])

  // 按下esc（或者其他监测不到用户交互的情况）进入3d鼠标模式时，需要手动点击鼠标/按下键盘，触发锁定鼠标
  const onVideoElementClickAndKeyDown = useCallback(
    (videoElementCase: HTMLVideoElement | null) => {
      if (
        ((isFullScreenRef.current && canUseMacFullScreenChromiumCompat) ||
          (isThreeDMouseRef.current &&
            !(isSafariBrowserVal && !isGameCloudComputer))) &&
        !document.pointerLockElement
      ) {
        const lockTimeStampOne = Date.now()

        // 解锁之后，再次锁定
        if (
          pointerLockActivatedAt.current &&
          pointerLockActivatedAtTwo.current &&
          lockTimeStampOne - pointerLockActivatedAt.current > 1000 &&
          lockTimeStampOne - pointerLockActivatedAtTwo.current > 1000
        ) {
          console.log("[mousePos],解锁后再次锁定====")

          requestPointerLockIfSupported(videoElementCase)
        }

        // 第一次锁定
        if (
          pointerLockActivatedAt.current === null ||
          pointerLockActivatedAtTwo.current === null
        ) {
          console.log("[mousePos],第一次锁定====")

          requestPointerLockIfSupported(videoElementCase)
        }
      }
    },
    [canUseMacFullScreenChromiumCompat, isSafariBrowserVal, isGameCloudComputer]
  )

  // 处理键盘事件
  useReportKeyboard(onVideoElementClickAndKeyDown, videoRef)
  // useReportKeyboard()

  useEffect(() => {
    isFullScreenRef.current = isFullScreen

    isThreeDMouseRef.current = isThreeDMouse

    const videoElement = videoRef.current

    if (shouldUseMacFullScreenVirtualMouse) {
      // mac+chrome内核+全屏：鼠标锁定
      onVideoElementClickAndKeyDown(videoElement)
    } else {
      // 鼠标解锁
      exitPointerLockIfSupported()

      const exitLockTimeStampOne = Date.now()
      pointerLockActivatedAt.current = exitLockTimeStampOne

      if (isThreeDMouse) {
        if (isSafariBrowserVal && !isGameCloudComputer) {
          // safari +非游戏电脑：不锁定鼠标
        } else {
          // 1、safari +游戏电脑 ：锁定 （会有横幅提示）
          // 2、其他场景：锁定
          onVideoElementClickAndKeyDown(videoElement)
        }
      } else {
        // 非3d鼠标，解锁
        exitPointerLockIfSupported()

        const exitLockTimeStampTwo = Date.now()
        pointerLockActivatedAt.current = exitLockTimeStampTwo
      }
    }

    document.onpointerlockchange = (event) => {
      if (document.pointerLockElement) {
        // console.log("==已锁定  鼠标===")
      } else {
        const exitLockTimeStampThree = Date.now()
        pointerLockActivatedAtTwo.current = exitLockTimeStampThree

        if (shouldUseMacFullScreenVirtualMouse) {
          // mac+chrome内核+全屏：解锁后 再次锁定
          onVideoElementClickAndKeyDown(videoElement)
        }

        if (isThreeDMouse) {
          // 3d鼠标模式 ：解锁后 再次锁定
          if (isSafariBrowserVal && !isGameCloudComputer) {
            // safari +非游戏电脑：不锁定鼠标
          } else {
            // 1、safari +游戏电脑 ：锁定
            // 2、其他场景：锁定
            onVideoElementClickAndKeyDown(videoElement)
          }
        }
      }
    }

    if (videoElement) {
      const handleVideoClick = () => onVideoElementClickAndKeyDown(videoElement)
      videoElement.addEventListener("click", handleVideoClick)

      return () => {
        videoElement.removeEventListener("click", handleVideoClick)
      }
    }

    return undefined
  }, [
    canUseMacFullScreenChromiumCompat,
    shouldUseMacFullScreenVirtualMouse,
    isThreeDMouse,
    isSafariBrowserVal,
    isGameCloudComputer,
  ])

  useEffect(() => {
    const videoElement = videoRef.current

    if (videoElement) {
      videoElement.addEventListener("wheel", onWheel, { passive: false })
      videoElement.addEventListener("mouseenter", onMouseEnter, {
        passive: false,
      })
      videoElement.addEventListener("mouseleave", onMouseLeave, {
        passive: false,
      })
      videoElement.addEventListener("mousemove", onMouseMove, {
        passive: false,
      })
      videoElement.addEventListener("mousedown", onMouseDown, {
        passive: false,
      })
      videoElement.addEventListener("mouseup", onMouseUp, { passive: false })
      // 监听 click 事件，用于拦截虚拟鼠标点击 UI 后的原生 click 事件
      videoElement.addEventListener("click", onClick, { passive: false })
    }

    const dispose = () => {
      if (videoElement) {
        videoElement.removeEventListener("wheel", onWheel)
        videoElement.removeEventListener("mouseenter", onMouseEnter)
        videoElement.removeEventListener("mouseleave", onMouseLeave)
        videoElement.removeEventListener("mousemove", onMouseMove)
        videoElement.removeEventListener("mousedown", onMouseDown)
        videoElement.removeEventListener("mouseup", onMouseUp)
        videoElement.removeEventListener("click", onClick)
      }
    }

    return dispose
  }, [videoViewLayout, isFullScreen, isThreeDMouse, isUseMacMouseWheelDir])

  // 初始化音频输入
  useInitAudioInput()
  // 初始化视频输入
  useInitVideoInput()

  // 全屏模式时启用捕获阶段事件拦截（防止 useOutsideClick 等监听器干扰虚拟鼠标点击）
  useEffect(() => {
    if (shouldUseMacFullScreenVirtualMouse) {
      const cleanup = VirtualMouseEvents.setupCaptureListeners(
        (_type, x, y, _button) => {
          // 更新 Redux 中的鼠标位置状态
          dispatch(updateMousePos({ x, y }))
        }
      )
      return cleanup
    }
  }, [shouldUseMacFullScreenVirtualMouse, dispatch])

  return (
    <>
      <video
        id="remote-desk-video"
        ref={videoRef}
        autoPlay
        muted
        style={{
          width: videoViewLayout.width,
          height: videoViewLayout.height,
          objectFit: "contain",
          objectPosition: "center",
          position: "absolute",
          left: 0,
          top: 0,
        }}
        onLoadedData={() => {
          // 新远端流已有可渲染帧后再移除冻结画面，避免重连期间黑屏。
          setReconnectingFrame(null)
          if (hasReportedFirstFrame.current) return
          hasReportedFirstFrame.current = true
          // 上报首帧实际可渲染的时间
          KCSDK.reportFirstFrameRender()
        }}
      />
      {reconnectingFrame !== null && (
        <img
          src={reconnectingFrame}
          alt=""
          style={{
            width: videoViewLayout.width,
            height: videoViewLayout.height,
            objectFit: "contain",
            objectPosition: "center",
            position: "absolute",
            left: 0,
            top: 0,
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
      )}
      <audio
        id="remote-audio"
        autoPlay
        muted
        ref={(el: HTMLAudioElement | null) => {
          // audioRef.current 是只读属性，用 Object.defineProperty 桥接 callback ref
          ;(audioRef as React.MutableRefObject<HTMLAudioElement | null>).current =
            el
        }}
      />
    </>
  )
}
